<template>
  <div :class="$setClasses">
    <div v-loading="bankTransferLoading"
         :class="$vClass('info')">
      <div v-for="(item, index) in paymentInfo" :class="$vClass('info__row')" :key="index">
        <div :class="$vClass('info__label')">{{ item.label }} :</div>
        <div :class="$vClass('info__value')">{{ item.value }}</div>
        <div :class="$vClass('info__copy')">
          <font-awesome-icon title="Copier" class="icon" @click="copyToClipboard(item.value)" :icon="['fal', 'copy']" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Offer } from '@/types/offer';
import { usePaymentsStore, useConfigStore } from '@/stores';
import { onMounted, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

defineOptions({
  name: 'CheckoutPayment',
  nameClassStyle: 'checkout-payment',
});

const { n } = useI18n();

const props = defineProps<{
  offer?: {type: Offer, default: () => ({})};
  bankData?: {
    financial_addresses: {
      iban: string;
      bic: string;
      account_holder_name: string;
    }[];
    reference: string;
    amount_remaining: number;
  };
  createBankTransfer?: boolean;
  forceNextButtonDisabled?: boolean;
}>();

const paymentsStore = usePaymentsStore();
const configStore = useConfigStore();

const { bankTransferLoading } = storeToRefs(paymentsStore);
const { nextButtonDisabled } = storeToRefs(configStore);

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
  ElMessage({
    message: 'Texte copié dans le pressse papier.',
    duration: 1000,
  });
};

const offer = computed(() => {
  return props.offer;
});

const paymentInfo = computed(() => {
  return [
    {
      label: 'Titulaire du compte',
      value: bankData.value?.account_holder_name,
    },
    {
      label: 'IBAN',
      value: bankData.value?.iban,
    },
    {
      label: 'BIC',
      value: bankData.value?.bic,
    },
    {
      label: 'Référence à renseigner',
      value: props.bankData?.reference,
    },
    {
      label: 'Montant restant',
      value: props.bankData?.amount_remaining ? n(props.bankData?.amount_remaining / 100, 'currency') : '',
    },
  ];
});

const bankData = computed(() => {
  return getIban(offer.value?.failed_payment?.nextAction?.display_bank_transfer_instructions || props.bankData || {});
});

const getIban = (item: { financial_addresses: { iban: string }[] }) => {
  return item?.financial_addresses?.[0]?.iban;
};

onMounted(async () => {
  try {
    nextButtonDisabled.value = true;
    if (props.createBankTransfer && offer.value?.id && (!bankData.value || Object.keys(bankData.value).length === 0)) {
      await paymentsStore.createBankTransfer(offer.value?.id);
    }
    nextButtonDisabled.value = props.forceNextButtonDisabled;
  } catch (error) {
    nextButtonDisabled.value = true;
  }
});
</script>

<style scoped lang="scss">
.checkout-payment {
  &__info {
    min-height: 100px;
    max-width: 600px;
    padding: 20px 10px;
    border-radius: 20px;
    background-color: var(--nxt-color-light-grey);
    display: grid;
    row-gap: 5px;

    @include breakpoint(tablet) {
      padding: 20px;
    }

    &__row {
      display: flex;
      column-gap: 3px;

      @include breakpoint(tablet) {
        column-gap: 10px;
      }
    }

    &__value {
      font-weight: 500;
      user-select: text;
    }

    &__copy {

      .icon {
        cursor: pointer;

        &:hover {
          font-size: 1.1em;
        }
      }
    }

    &__copy {
      flex: 1;
      text-align: end;
    }
  }
}
</style>
